import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RorosActorService } from '@/services/RorosService';
import { rorosKeys } from './keys';
import type {
  CreateActorPayload,
  UpdateActorPayload,
  ChangeAdoptionLevelPayload,
  ConsultPersonaPayload,
  CouncilConsultPayload,
  CreateReadinessAssessmentPayload,
  UpsertActorPersonaPayload,
} from '@/types/roros/actors';

const STALE_TIME = 2 * 60_000;
const GC_TIME = 5 * 60_000;

// ─── Queries ───────────────────────────────────────────────────────────────

export function useRorosActors(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.actors.all, params] as const,
    queryFn: () => RorosActorService.list(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosActor(id: string) {
  return useQuery({
    queryKey: rorosKeys.actors.detail(id),
    queryFn: () => RorosActorService.get(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosConsultations(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.actors.consultations, params] as const,
    queryFn: () => RorosActorService.listConsultations(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosConsultation(id: string) {
  return useQuery({
    queryKey: rorosKeys.actors.consultation(id),
    queryFn: () => RorosActorService.getConsultation(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────────

export function useRorosActorCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateActorPayload) => RorosActorService.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.actors.all });
      // When an ai_operator actor is created, the server atomically inserts a
      // placeholder roros_agent_definitions row in the same transaction
      // (actors.service.ts createActor → "create agent_definitions placeholder").
      // The AiOperatorsPage builds its definitionByActorId map from the agents
      // query — if we do not invalidate that query here, the page sees a stale
      // map, the new card opens the editor with `agentDefinition === null`,
      // the Save button reads "Create", and the POST to /agent-definitions hits
      // the UNIQUE(actor_id) constraint with a 409. Symptom for the user: the
      // editor for a just-created operator says "Create", Save appears to do
      // nothing (toast only, sheet stays open). Invalidating both queries here
      // closes that gap regardless of which actor_type was created.
      qc.invalidateQueries({ queryKey: rorosKeys.agents.all });
    },
  });
}

export function useRorosActorUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateActorPayload }) =>
      RorosActorService.update(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.actors.all });
      qc.invalidateQueries({ queryKey: rorosKeys.actors.detail(id) });
    },
  });
}

export function useRorosActorDeactivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosActorService.deactivate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.actors.all });
    },
  });
}

export function useRorosActorReactivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosActorService.reactivate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.actors.all });
    },
  });
}

export function useRorosActorArchive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosActorService.archive(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.actors.all });
    },
  });
}

export function useRorosActorAssignDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, domainId }: { id: string; domainId: string }) =>
      RorosActorService.assignDomain(id, { domainId }),  // A1
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.actors.all });
      qc.invalidateQueries({ queryKey: rorosKeys.actors.detail(id) });
    },
  });
}

export function useRorosActorUnassignDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, domainId }: { id: string; domainId: string }) =>
      RorosActorService.unassignDomain(id, domainId),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.actors.all });
      qc.invalidateQueries({ queryKey: rorosKeys.actors.detail(id) });
    },
  });
}

export function useRorosActorChangeAdoptionLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ChangeAdoptionLevelPayload }) =>
      RorosActorService.changeAdoptionLevel(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.actors.all });
      qc.invalidateQueries({ queryKey: rorosKeys.actors.detail(id) });
    },
  });
}

export function useRorosConsultPersona() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ personaId, payload }: { personaId: string; payload: ConsultPersonaPayload }) =>
      RorosActorService.consultPersona(personaId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.actors.consultations });
    },
  });
}

export function useRorosConsultCouncil() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CouncilConsultPayload) => RorosActorService.consultCouncil(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.actors.consultations });
    },
  });
}

export function useRorosActorReadinessAssessments(actorId: string, params?: Record<string, string>) {
  return useQuery({
    queryKey: rorosKeys.actors.readinessAssessments(actorId),
    queryFn: () => RorosActorService.listReadinessAssessments(actorId, params),
    enabled: !!actorId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosActorCreateReadinessAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ actorId, payload }: { actorId: string; payload: CreateReadinessAssessmentPayload }) =>
      RorosActorService.createReadinessAssessment(actorId, payload),
    onSuccess: (_data, { actorId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.actors.readinessAssessments(actorId) });
      qc.invalidateQueries({ queryKey: rorosKeys.actors.detail(actorId) });
    },
  });
}

// ─── Wave B AC-MOD-2: Org moderator defaults hooks ──────────────────────────

export function useRorosOrgModeratorDefaults() {
  return useQuery({
    queryKey: rorosKeys.actors.orgModeratorDefaults,
    queryFn: () => RorosActorService.getOrgModeratorDefaults(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosSetOrgMeetingModerator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (actorId: string | null) => RorosActorService.setOrgMeetingModerator(actorId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.actors.orgModeratorDefaults });
      qc.invalidateQueries({ queryKey: rorosKeys.actors.all });
    },
  });
}

export function useRorosSetOrgDecisionModerator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (actorId: string | null) => RorosActorService.setOrgDecisionModerator(actorId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.actors.orgModeratorDefaults });
      qc.invalidateQueries({ queryKey: rorosKeys.actors.all });
    },
  });
}

// ─── F-Meeting-1 Wave 1: Actor Persona hooks ────────────────────────────────

export function useRorosActorPersona(actorId: string) {
  return useQuery({
    queryKey: rorosKeys.actors.actorPersona(actorId),
    queryFn: () => RorosActorService.getPersona(actorId),
    enabled: !!actorId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosActorPersonaUpsert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ actorId, payload }: { actorId: string; payload: UpsertActorPersonaPayload }) =>
      RorosActorService.upsertPersona(actorId, payload),
    onSuccess: (_data, { actorId }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.actors.actorPersona(actorId) });
    },
  });
}
